import {GanttChartRows} from '@hconnect/ganttchart/src/modules'
import {GanttChartStaticItem} from '@hconnect/ganttchart/src/modules/GanttChartStaticItem'
import {GanttChartBaseRow} from '@hconnect/ganttchart/src/types/common'
import {round} from 'lodash'
import {memo, RefObject, useCallback, useMemo, useRef} from 'react'

import {DrillingChartTooltip} from './DrillingChartTooltip'
import {DrillingModal} from './DrillingModal'

import {useRefraModal} from '@refra/modules/common/providers'
import {
  drawDrillingZoneLines,
  DRILLING_SETTINGS_CANVAS_HEIGHT
} from '@refra/modules/refra-planning/components/KilnOverviewCard/Drilling/DrillingSettings/DrillingZones/helpers'
import {mapColorConfig} from '@refra/modules/refra-planning/helpers/mapColorConfig'
import {useKilnSettings} from '@refra/modules/refra-planning/providers'
import {KilnShutdown, ShutdownDrillingDTO} from '@refra/modules/refra-planning/types'

const DEFAULT_CANVAS_HEIGHT = 50
const DRILLING_ROW_HEIGHT = 35
const ITEM_SIZE = 4

interface DrillingChartProps {
  shutdown: KilnShutdown
  stepWidth: number
  kilnLength: number
  xAxis: number[]
  drillings: ShutdownDrillingDTO[]
  ganttContainerRef?: RefObject<HTMLDivElement>
  isReadOnly?: boolean
}

const _DrillingChart = ({
  shutdown,
  stepWidth,
  kilnLength,
  xAxis,
  ganttContainerRef,
  drillings,
  isReadOnly = false
}: DrillingChartProps) => {
  const {kilnConfig, drillingSettings} = useKilnSettings()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const {openModal} = useRefraModal()
  const ganttChartRows = [
    {
      id: 1,
      name: ''
    }
  ]

  const renderCallback = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const zones = drillingSettings.drillingZones

      if (!ctx || !zones || zones.length === 0) return

      // Configure canvas styles
      ctx.setLineDash([4, 4])
      ctx.strokeStyle = 'red'
      ctx.lineWidth = 1.2

      drawDrillingZoneLines({
        ctx,
        zones,
        stepWidth,
        canvasHeight: DEFAULT_CANVAS_HEIGHT
      })
    },
    [drillingSettings.drillingZones, stepWidth]
  )

  const colorConfig = mapColorConfig(kilnConfig, stepWidth, [1])

  const calculateItemRowPadding = (item: ShutdownDrillingDTO) => {
    const heightStep = round(DRILLING_SETTINGS_CANVAS_HEIGHT / DEFAULT_CANVAS_HEIGHT, 2)
    return DEFAULT_CANVAS_HEIGHT - ITEM_SIZE / 2 - item.depth / heightStep
  }

  const calculateItemColor = useCallback(
    (item: ShutdownDrillingDTO, defaultColor?: string, negativeColor?: string) => {
      const drillingZone = drillingSettings.drillingZones.find(
        (z) => z.fromMeter <= item.meter && z.toMeter >= item.meter
      ) ?? {threshold: 0}

      const negativeC = negativeColor ?? 'bg-negative'
      const defaultC = defaultColor ?? 'bg-foreground_faded'

      return item.depth < drillingZone?.threshold ? negativeC : defaultC
    },
    [drillingSettings.drillingZones]
  )

  const drillingsByMeter = useMemo(
    () => (drillings ? Object.groupBy(drillings, (item) => item.meter) : []),
    [drillings]
  )

  const displayGanttChartItem = useCallback(
    (item: ShutdownDrillingDTO) =>
      item.id ? (
        <GanttChartStaticItem
          key={`${shutdown.id}-${item.id}-drillings-item`}
          item={{
            id: item.id.toString(),
            start: item.meter * stepWidth - ITEM_SIZE,
            end: item.meter * stepWidth,
            label: (
              <div
                className={`w-[${ITEM_SIZE}px] h-[${ITEM_SIZE}px] ${calculateItemColor(
                  item
                )} rotate-45`}
              />
            ),
            height: ITEM_SIZE,
            tooltip: {
              tooltipContent: (
                <DrillingChartTooltip
                  meter={item.meter}
                  drillingsByMeter={drillingsByMeter}
                  calculateItemColor={calculateItemColor}
                />
              )
            },
            testPrefix: 'drillings-'
          }}
          rowHeight={DRILLING_ROW_HEIGHT}
          rowPadding={calculateItemRowPadding(item)}
          stepWidth={stepWidth}
          mutationKey={'editKilnReplacement'}
        />
      ) : null,
    [calculateItemColor, drillingsByMeter, shutdown.id, stepWidth]
  )

  return (
    <div ref={ganttContainerRef} className="w-full h-[60px] overflow-hidden relative">
      <GanttChartRows<GanttChartBaseRow, number>
        chartStartBoundary={0}
        chartEndBoundary={kilnLength}
        stepWidth={stepWidth}
        isReadOnly={isReadOnly}
        xAxis={xAxis}
        colorConfig={colorConfig}
        onAddItem={(item) => {
          openModal(
            <DrillingModal
              shutdown={shutdown}
              drillings={drillings}
              meter={item.selectedItemXAxisValue}
              drillingPositions={drillingSettings.drillingPositions}
              kilnLength={kilnLength}
            />
          )
        }}
        ganttChartRows={ganttChartRows}
        canvasRef={canvasRef}
        onRenderCallback={renderCallback}
      />

      {drillings && drillings.length > 0 && (
        <div className="absolute w-full">
          <div className="w-full relative h-full">
            {drillings.map((item) => displayGanttChartItem(item))}
          </div>
        </div>
      )}
    </div>
  )
}

export const DrillingChart = memo(_DrillingChart)
